import { z } from "zod";
import { leadRowSchema, listOffsetSchema } from "./leads.js";
import { personSchema } from "./people.js";
import { taskViewSchema } from "./task-view.js";
import { idSchema, multiLineTextSchema } from "./text.js";
// I richiami e l'agenda dei Task sul server (piano M5, Task 16; D12, D13, D14, D17). Le rotte:
// - `GET /api/leads/:leadId/callback-slots`: la finestra «Richiama dopo» calcolata dal server, nel fuso dell'azienda (D13);
// - `POST /api/leads/:leadId/tasks`: «Richiama dopo» fissa il richiamo (un solo richiamo aperto per Lead: `409 task_exists`);
// - `PATCH /api/tasks/:taskId` («Sposta»), `POST /api/tasks/:taskId/complete` («Fatto» a mano), `POST /api/tasks/:taskId/cancel`;
// - `GET /api/tasks`: «Le cose da fare», con le quattro schede, «Di chi», «Cosa», lo status del Lead e l'ordine;
// - `GET /api/tasks/due-now`: i richiami concordati di chi guarda con l'ora arrivata, per il banner (D14).
// Un richiamo si completa da solo quando una chiamata in uscita al Lead, nata dopo di lui, finisce arrivando al Lead (ruling Q2,
// Q55): non c'è una rotta per questo.
//
// Gli elenchi ripetono quelli del dominio (`@gioia365/domain`: `callbacks.ts`), che i contratti non importano: un test del server
// li confronta voce per voce.
/** I 16 blocchi «Tra qualche ora»: da +30 minuti a +8 ore di lavoro negli orari dei richiami. */
export const CALLBACK_SOON_PRESETS = ["+30m", "+1h", "+1h30", "+2h", "+2h30", "+3h", "+3h30", "+4h", "+4h30", "+5h", "+5h30", "+6h", "+6h30", "+7h", "+7h30", "+8h"];
/**
 * Perché un orario non è un richiamo valido (`422 callback_slot_invalid` con `details.reason`), nell'ordine in cui il server li
 * controlla: non dopo adesso, un giorno chiuso, fuori dagli orari dei richiami, non a quarti d'ora, troppo lontano.
 */
export const CALLBACK_INVALID_REASONS = ["past", "closed_day", "outside_hours", "not_quarter", "too_far"];
/**
 * La finestra «Richiama dopo» (D13): i 16 blocchi con l'orario già calcolato (istanti ISO), i due giorni lavorativi successivi
 * (`AAAA-MM-GG` del giorno dell'azienda) con un orario ogni mezz'ora, i limiti di «Altro giorno e ora» (a quarti d'ora, dal primo
 * quarto valido fino a `latestDay` compreso).
 */
export const callbackSlotsResponseSchema = z.object({
    soon: z.array(z.object({ preset: z.enum(CALLBACK_SOON_PRESETS), at: z.string() })),
    days: z.array(z.object({ day: z.string(), slots: z.array(z.string()) })),
    custom: z.object({ stepMinutes: z.literal(15), earliest: z.string(), latestDay: z.string() }),
});
/**
 * «Richiama dopo»: l'istante scelto (ISO, rivalidato dal server), da quale scelta della finestra viene, «Concordato con il Lead»
 * (parte sempre vuota), la chiamata da cui nasce (la postazione, con l'esito «Richiamare»; vuota dalla scheda), una nota e la
 * chiave d'idempotenza: lo stesso `requestId` risponde lo stesso Task (diventa il suo identificativo).
 */
export const createCallbackBodySchema = z.strictObject({
    at: z.iso.datetime(),
    preset: z.union([z.enum(CALLBACK_SOON_PRESETS), z.literal("day_slot"), z.literal("custom")]),
    agreedWithLead: z.boolean(),
    sourceCallId: idSchema.optional(),
    note: multiLineTextSchema(500).nullable(),
    requestId: idSchema,
});
/** «Sposta»: il nuovo istante e «Concordato con il Lead», con la versione letta. */
export const rescheduleTaskBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1), at: z.iso.datetime(), agreedWithLead: z.boolean() });
/** «Fatto» e «Annulla»: la versione letta. */
export const taskActionBodySchema = z.strictObject({ expectedVersion: z.number().int().min(1) });
/** La risposta di «Richiama dopo», «Sposta», «Fatto» e «Annulla»: il Task com'è dopo. */
export const taskResponseSchema = z.object({ task: taskViewSchema });
/** `409 task_exists`: il richiamo aperto che il Lead ha già, che l'interfaccia offre di spostare. */
export const taskExistsDetailsSchema = z.object({ taskId: z.string() });
/**
 * `GET /api/tasks` (D12, D17; mock-up `task.html`): la scheda («Oggi» con le scadute in cima, «Scaduti», «Prossimi giorni»,
 * «Completati»), «Di chi» (`all` o una persona, soltanto per chi vede tutti i Task), «Cosa» (`calls`: i richiami, `disco` e `demo`: gli appuntamenti; prima di R7 ogni
 * Task è un richiamo), lo status del Lead, l'ordine mostrato (`due`: come vanno fatti) e la pagina.
 */
export const tasksQuerySchema = z.strictObject({
    view: z.enum(["today", "overdue", "upcoming", "done"]).default("today"),
    who: z.union([z.literal("all"), idSchema]).optional(),
    kind: z.enum(["all", "calls", "disco", "demo"]).default("all"),
    status: idSchema.optional(),
    sort: z.enum(["due", "priority", "lead", "status"]).default("due"),
    offset: listOffsetSchema,
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Una riga dell'agenda: il Task, il suo Lead come nell'elenco dei Lead (nome, salone, città, status, owner, ultima chiamata),
 * se si mostra l'ora o «scaduto», e il posto nella cascata dei Task (R4: `null` finché la cascata dai Task non c'è).
 */
export const taskRowSchema = z.object({
    task: taskViewSchema,
    lead: z.object({
        id: z.string(),
        displayName: z.string().nullable(),
        salonName: z.string().nullable(),
        city: leadRowSchema.shape.city,
        status: leadRowSchema.shape.status,
        owner: personSchema.nullable(),
        latestCall: leadRowSchema.shape.latestCall,
    }),
    dueKind: z.enum(["time", "overdue"]),
    position: z.number().int().nullable() /* R4 */,
});
/** La pagina dell'agenda, quanti Task ha la scheda (`total`), quanti ne ha ognuna delle quattro e se si sceglie «Di chi». */
export const tasksResponseSchema = z.object({
    rows: z.array(taskRowSchema),
    total: z.number().int(),
    counts: z.object({ today: z.number().int(), overdue: z.number().int(), upcoming: z.number().int(), done: z.number().int() }),
    canFilterByPerson: z.boolean(),
    offset: z.number().int(),
    limit: z.number().int(),
});
/** `GET /api/tasks/due-now` (D14): i richiami concordati di chi guarda con l'ora arrivata, il più vecchio prima. */
export const dueNowResponseSchema = z.object({ tasks: z.array(taskRowSchema) });
//# sourceMappingURL=tasks.js.map